import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, Filter, GripVertical } from "lucide-react";
import { ProDatePicker } from "./ProDatePicker";
import { CustomSelect } from "./CustomSelect";
import { useEffect, useMemo, useState } from "react";

const TEXT_OPERATORS = [
  { label: "Contains", value: "contains" },
  { label: "Equals", value: "equals" },
  { label: "Starts with", value: "starts_with" },
];

const DATE_OPERATORS = [
  { label: "After", value: "after" },
  { label: "Before", value: "before" },
];

const NUMBER_OPERATORS = [
  { label: "Greater than", value: "greater_than" },
  { label: "Less than", value: "less_than" },
  { label: "Equals", value: "equals" },
];

const SortableColumnRow = ({
  column,
  isExpanded,
  onExpand,
}: {
  column: any;
  isExpanded: boolean;
  onExpand: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });
  const columnType = column.columnDef.meta?.type;
  const isDate = columnType === "date" || columnType === "datetime";
  const isNumber = columnType === "number";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  const isVisible = column.getIsVisible();
  const filterValue =
    column.getFilterValue() ||
    (isDate
      ? [
          { operator: "after", value: "", type: columnType },
          { operator: "before", value: "", type: columnType },
        ]
      : isNumber
        ? { operator: "greater_than", value: "", type: columnType }
        : { operator: "contains", value: "", type: columnType });
  const [localValue, setLocalValue] = useState(filterValue.value ?? "");

  const isFilterActive = isDate
    ? filterValue.some((f: any) => f.value && f.value !== "")
    : filterValue.value !== undefined && filterValue.value !== "";

  const updateFilter = (val: any) => column.setFilterValue(val);

  useEffect(() => {
    if (filterValue.value !== localValue) {
      setLocalValue(filterValue.value ?? "");
    }
  }, [filterValue.value]);

  const handleValueChange = (val: string) => {
    setLocalValue(val);

    // We must push the update to the table IMMEDIATELY so the dot shows up
    if (isDate) {
      // ... handled in Date pickers
    } else {
      // This tells the Table (and the Header Dot) that a filter exists
      updateFilter({ ...filterValue, value: val });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex flex-col gap-2 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl mb-2"
    >
      <div className="flex items-center gap-3 p-1">
        <div {...attributes} {...listeners} className="cursor-grab p-1">
          <GripVertical size={20} className="dark:text-slate-300" />
        </div>
        <span className="flex-1 text-sm font-bold dark:text-slate-300">
          {column.columnDef.header}
        </span>
        <button
          onClick={onExpand}
          className={`relative p-2 rounded-lg transition-all ${
            isExpanded
              ? "text-primary bg-primary/10"
              : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <Filter size={18} />

          {/* Active Filter Dot */}
          {isFilterActive && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary border-2 border-white dark:border-slate-900 rounded-full animate-in zoom-in duration-300" />
          )}
        </button>
        <button
          onClick={() => column.toggleVisibility()}
          className={`p-2 rounded-lg transition-colors ${isVisible ? "text-primary bg-primary/10" : "text-slate-300 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
        >
          {isVisible ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {isDate ? (
            <div className="space-y-2">
              {filterValue.map((f: any, i: number) => (
                <div key={i} className="flex gap-2">
                  <CustomSelect
                    className="w-32"
                    value={f.operator}
                    options={DATE_OPERATORS}
                    onChange={(val) => {
                      const next = [...filterValue];
                      next[i].operator = val;
                      updateFilter(next);
                    }}
                  />
                  <ProDatePicker
                    value={f.value}
                    onChange={(v) => {
                      const next = [...filterValue];
                      next[i].value = v;
                      updateFilter(next);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex gap-2">
              <CustomSelect
                className="w-32"
                value={filterValue.operator}
                options={isNumber ? NUMBER_OPERATORS : TEXT_OPERATORS}
                onChange={(val) => {
                  // Ensure type is preserved when changing operator
                  updateFilter({
                    ...filterValue,
                    operator: val,
                    type: columnType,
                  });
                }}
              />
              <input
                type="text"
                inputMode={isNumber ? "numeric" : "text"}
                value={localValue}
                onChange={(e) => {
                  let val = e.target.value;
                  if (isNumber) val = val.replace(/[^0-9.]/g, "");
                  setLocalValue(val);
                }}
                onBlur={() => {
                  updateFilter({
                    ...filterValue,
                    value: localValue,
                    type: columnType,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    updateFilter({
                      ...filterValue,
                      value: localValue,
                      type: columnType,
                    });
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder={isNumber ? "0" : "Search..."}
                className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SortableColumnRow;
